<script lang="ts">
  import { navigate } from '../router.svelte';
  import { QUAD, ROLE_ORDER, TERMINAL_ROLE } from '@ui/quadrant';
  import type { CensusBlock, CensusSparkBlock, RoleVolumeBlock } from '@api';
  import StatCard from './StatCard.svelte';
  import RoleIcon from '@ui/RoleIcon.svelte';

  // Full-census contributor-role strip: one StatCard per live quadrant, showing the
  // current count and net change over the window, coloured by the quadrant hue,
  // followed by the terminal `departed` figure set apart from them. Shared by the
  // Overview and Contributors pages so the census renders identically in both.
  let {
    census,
    spark,
    roleVolume,
  }: { census: CensusBlock; spark: CensusSparkBlock; roleVolume: RoleVolumeBlock } = $props();

  const nf = new Intl.NumberFormat();
  const roleCards = $derived.by(() =>
    ROLE_ORDER.map((r) => {
      const sp = spark.roles[r] ?? [];
      // The count is the CENSUS (`census.roles`) — the same 30-day role map behind
      // the badges, the avatar rings, the members table and the "See X →" link below,
      // so the card can't say 135 spectators where the list says 65. The sparkline
      // trail is a different population by design (historical: it keeps members who
      // have since left), so the delta is read from the trail alone rather than
      // subtracting one population from the other.
      const now = census.roles[r] ?? (sp.length ? (sp.at(-1) ?? 0) : 0);
      const d = (sp.at(-1) ?? 0) - (sp[0] ?? 0);
      // Cumulative posts + comments this group produced over the period.
      const vol = roleVolume[r];
      const volSub = vol ? `${nf.format(vol.posts)} posts · ${nf.format(vol.comments)} comments` : null;
      return {
        role: r,
        meta: QUAD[r],
        value: String(now),
        count: now,
        delta: sp.length >= 2 ? `${d >= 0 ? '+' : ''}${d}` : null,
        spark: sp,
        volSub,
      };
    }),
  );
  // Every member still here is classified, so the counts sum to the current base.
  const censusTotal = $derived(roleCards.reduce((sum, c) => sum + c.count, 0));

  // The terminal role, shown as its own figure BELOW the census rather than as a
  // sixth card. It is not a contributor mix: on a swept community it is regularly
  // the largest bucket (71 vs 10/3/3 on the first one), so inline it would squash
  // the four live roles into slivers and answer "who carries this community?" with
  // "people who left". Absent (null) when the API reports no departed count — an
  // unswept community has no membership data, and a fabricated 0 would read as
  // "nobody has ever left", which is a claim we can't make.
  const departed = $derived.by(() => {
    const n = census.roles[TERMINAL_ROLE];
    if (n == null || n <= 0) return null;
    const vol = roleVolume[TERMINAL_ROLE];
    const wrote = vol && (vol.posts || vol.comments)
      ? `${nf.format(vol.posts)} posts · ${nf.format(vol.comments)} comments`
      : null;
    return { meta: QUAD[TERMINAL_ROLE], value: nf.format(n), wrote };
  });
</script>

<div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
  <span class="eyebrow text-base-content/65">Contributor roles</span>
  <span class="text-xs text-base-content/65">
    every one of {censusTotal.toLocaleString()} members here now · inactive → spectator / newcomer
  </span>
</div>
<div class="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
  {#each roleCards as c (c.role)}
    <StatCard
      label={c.meta.label}
      value={c.value}
      delta={c.delta}
      color={c.meta.color}
      info={c.meta.tip}
      sub={c.volSub}
      spark={c.spark}
      sparkLabels={spark.role_labels ?? spark.labels}
      sparkFormat={(v) => `${v} members`}
    >
      {#snippet icon()}<RoleIcon name={c.meta.icon} />{/snippet}
      {#snippet action()}
        <button
          class="inline-flex items-center gap-0.5 text-[0.7rem] font-medium text-base-content/65 transition-colors hover:text-primary"
          onclick={() => navigate(`/admin/community/members?quadrant=${c.role}`)}
        >See {c.meta.label.toLowerCase()}s <span aria-hidden="true">→</span></button>
      {/snippet}
    </StatCard>
  {/each}
</div>

{#if departed}
  {@const d = departed}
  <!-- Terminal role: outside the grid, dashed and muted, so it reads as "no longer
       in the room" rather than as a sixth kind of contributor. -->
  <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-box border border-dashed hair bg-base-200/30 px-4 py-2.5">
    <span class="flex shrink-0 items-baseline gap-1.5" style:color={d.meta.color}>
      <span class="self-center"><RoleIcon name={d.meta.icon} size={15} /></span>
      <span class="font-display text-xl font-bold leading-none tabular-nums">{d.value}</span>
      <span class="eyebrow">{d.meta.label}</span>
    </span>
    <span class="min-w-0 flex-1 text-xs text-base-content/65">
      Not counted above — they're no longer members. Everything they wrote is kept{#if d.wrote}, including {d.wrote} in the last 30 days{/if}.
    </span>
    <button
      class="inline-flex shrink-0 items-center gap-0.5 text-[0.7rem] font-medium text-base-content/65 transition-colors hover:text-primary"
      onclick={() => navigate(`/admin/community/members?quadrant=${TERMINAL_ROLE}`)}
    >See who left <span aria-hidden="true">→</span></button>
  </div>
{/if}
